/**
 * فرم شرح تصمیم — ورودی مرحله‌ی اول.
 *
 * نمونه‌های آماده از پروفایل می‌آیند، نه از کد. کاربر تازه‌وارد باید بتواند
 * بی‌مقدمه شروع کند؛ صفحه‌ی خالی با یک ناحیه‌ی متن، او را متوقف می‌کند.
 */

import { PenLine, Send, Sparkles } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Banner, Card, SectionHeader } from '@/components/ui/primitives'
import { useMeta } from '@/lib/meta-context'

interface CaseFormProps {
  onSubmit: (caseText: string) => void
  isBusy: boolean
  /** متن اولیه، برای وقتی که از آرشیو «پرسیدن دوباره» انجام شده. */
  initialText?: string
}

export const CaseForm = ({ onSubmit, isBusy, initialText = '' }: CaseFormProps) => {
  const { meta } = useMeta()
  const [text, setText] = useState(initialText)
  const examples = meta.profile.example_cases

  return (
    <div className="space-y-4">
      <Banner tone="info">{meta.profile.disclaimer}</Banner>

      {/* کارت شاخص: تنها ناحیه‌ی اصلی این مرحله است، پس تأکید بصری می‌گیرد. */}
      <Card tone="feature">
        <SectionHeader
          icon={<PenLine className="size-4" aria-hidden />}
          title="تصمیم جاری"
          caption="مسئله، گزینه‌ها، محدودیت‌ها و نتیجه‌ی مطلوب را بنویسید. هر چه شرح کامل‌تر باشد، برآورد دقیق‌تر است."
        />
        <form
          className="relative space-y-4 p-5"
          onSubmit={(event) => {
            event.preventDefault()
            if (text.trim() !== '') {
              onSubmit(text)
            }
          }}
        >
          {examples.length > 0 && (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-muted">
                <Sparkles className="size-3.5 text-accent" aria-hidden />
                شروع سریع
              </p>
              <div className="flex flex-wrap gap-2">
                {examples.map((example) => (
                  // چیپِ نمونه، دکمه‌ی معمول نیست: عمل ثانویه‌ی سبک است و نباید
                  // با دکمه‌ی اصلی فرم هم‌وزن دیده شود.
                  <button
                    key={example.label}
                    type="button"
                    onClick={() => {
                      setText(example.text)
                    }}
                    className="press rounded-full border border-line bg-surface-sunken px-3.5 py-1.5 text-xs text-ink-muted transition-colors hover:border-accent-line hover:bg-accent-soft hover:text-accent"
                  >
                    {example.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="case-text" className="text-xs font-semibold text-ink">
              شرح تصمیم
            </label>
            <textarea
              id="case-text"
              value={text}
              onChange={(event) => {
                setText(event.target.value)
              }}
              rows={10}
              placeholder={meta.profile.input_placeholder}
              className="w-full resize-y rounded-md border border-line-strong bg-surface-raised px-4 py-3 text-sm leading-relaxed text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
            <p className="max-w-md text-xs leading-relaxed text-ink-faint">
              حداکثر سه پرسش شفاف‌سازی پرسیده می‌شود، و تنها اگر پاسخش جهت تصمیم را عوض کند.
            </p>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              icon={<Send className="size-4" aria-hidden />}
              loading={isBusy}
              disabled={text.trim() === ''}
            >
              بررسی پرونده
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
